/**
 * How far a questionnaire step is from a screen that offers a free
 * consultation, as one sentence for the chat assistant's page description.
 *
 * The counts in this table are approximate. They were worked out by hand from
 * sequence 6ddb207c-0acc-4c21-b84e-7951263c8f99, which patient-backend keeps in
 * fixtures/sequence.160626.lockezfileonyes.jsonm. The sequence is a branching
 * tree, so the number of questions between two screens depends on earlier
 * answers; each range runs from the shortest route to the longest usual one.
 * The table must be revisited whenever patient-backend changes the sequence,
 * because the app cannot see the sequence ahead of the visitor.
 *
 * A count includes the step the visitor is on, because they still have to
 * answer it. Loading screens are not counted as questions and have no note.
 *
 * These screens offer to schedule a consultation. Validation shows "Schedule a
 * Call" beside "Continue Online". Consult focus shows "Schedule Consultation
 * Now". A product step opens a consultation dialog from "Lower My Bills".
 * Checkout shows a "Schedule Call" card. The in-collections question shows a
 * "Schedule a free consult" banner when the visitor's bill is $5,000 or more.
 * Saving estimate and offer consult also offer one, although the current
 * sequence does not include them.
 *
 * In the current sequence, visitors whose bill is under $5,000 are never
 * offered a consultation by the questionnaire: they skip validation, never see
 * the banner, and every route for them ends before consult focus. The counts
 * below therefore describe visitors with larger bills, and the sentence says
 * that not every visitor is offered one.
 *
 * The path names what part of the questionnaire a step belongs to, only where
 * the sequence makes that certain. The concierge path is the one that leads to
 * Resolve's bill-reduction service and checkout. EZ File is the financial-aid
 * filing path; the financial-aid step offers it, and a visitor who accepts it
 * leaves the questionnaire there.
 */

import { StepName } from '@/state/state.types';

type SchedulingPath = 'concierge' | 'ez-file-eligibility' | 'ez-file-choice';

type SchedulingNote =
  | {
      offer: 'on-this-screen';
      // A condition, written as a clause, under which the screen shows the offer.
      onlyWhen?: string;
      path?: SchedulingPath;
    }
  | {
      offer: 'after-questions';
      // The fewest and the most questions, counting the current step, that a
      // visitor answers before a screen offers a consultation.
      fewest: number;
      most: number;
      path?: SchedulingPath;
    };

export const schedulingNotes: Partial<Record<StepName, SchedulingNote>> = {
  // Bills of $15,000 or more go straight to validation; bills of $5,000 to
  // $15,000 answer max-bill-size-2 first.
  [StepName.bill_size]: { offer: 'after-questions', fewest: 1, most: 2 },
  [StepName.max_bill_size_2]: { offer: 'after-questions', fewest: 1, most: 1 },
  [StepName.validation]: { offer: 'on-this-screen' },
  // From email the visitor answers email, complete-account,
  // medical-provider-name, household-size and above-fpl. Then either the
  // in-collections banner or consult focus offers a consultation, sometimes
  // after one bill-date or discharged-date question.
  [StepName.email]: { offer: 'after-questions', fewest: 5, most: 6 },
  [StepName.complete_account]: { offer: 'after-questions', fewest: 4, most: 5 },
  [StepName.medical_provider_name]: { offer: 'after-questions', fewest: 3, most: 4 },
  [StepName.household_size]: { offer: 'after-questions', fewest: 2, most: 3 },
  [StepName.above_fpl]: { offer: 'after-questions', fewest: 1, most: 2 },
  [StepName.in_collections]: {
    offer: 'on-this-screen',
    onlyWhen: 'if their bill is $5,000 or more',
  },
  // A "no" goes straight to consult focus. A "yes" may add a date question,
  // then the financial-aid step, and, if EZ File is declined without a
  // rejection, the two concierge questions before a product step.
  [StepName.is_us_resident]: {
    offer: 'after-questions',
    fewest: 1,
    most: 5,
    path: 'ez-file-eligibility',
  },
  [StepName.is_state_residnet]: {
    offer: 'after-questions',
    fewest: 1,
    most: 5,
    path: 'ez-file-eligibility',
  },
  // A "no" goes straight to consult focus. A "yes" leads to the financial-aid
  // step, and possibly the two concierge questions before a product step.
  [StepName.bill_date]: { offer: 'after-questions', fewest: 1, most: 4 },
  [StepName.discharged_date]: { offer: 'after-questions', fewest: 1, most: 4 },
  // "I Was Already Rejected" leads to consult focus. Declining EZ File leads to
  // pain-point-select-001 and concerns-select, then a product step. Accepting
  // EZ File ends the questionnaire with no offer.
  [StepName.financial_aid]: {
    offer: 'after-questions',
    fewest: 1,
    most: 3,
    path: 'ez-file-choice',
  },
  [StepName.consult_focus]: { offer: 'on-this-screen', path: 'concierge' },
  // After consult focus come intake-notes, legal-encumbrance,
  // pain-point-select-001 and concerns-select, then a product step.
  [StepName.intake_notes]: {
    offer: 'after-questions',
    fewest: 4,
    most: 4,
    path: 'concierge',
  },
  [StepName.legal_encumbrance]: {
    offer: 'after-questions',
    fewest: 3,
    most: 3,
    path: 'concierge',
  },
  [StepName.pain_point_select_001]: {
    offer: 'after-questions',
    fewest: 2,
    most: 2,
    path: 'concierge',
  },
  [StepName.concerns]: { offer: 'after-questions', fewest: 1, most: 1, path: 'concierge' },
  [StepName.product]: { offer: 'on-this-screen', path: 'concierge' },
  [StepName.checkout]: { offer: 'on-this-screen', path: 'concierge' },
  [StepName.saving_estimate]: { offer: 'on-this-screen' },
  [StepName.offer_consult]: { offer: 'on-this-screen' },
};

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'];

const inWords = (count: number) => NUMBER_WORDS[count] ?? String(count);

// Adjacent counts read as "one or two"; wider ranges read as "one to five".
const describeRange = (fewest: number, most: number) => {
  if (fewest === most) return `about ${inWords(fewest)}`;
  const joiner = most - fewest === 1 ? 'or' : 'to';
  return `roughly ${inWords(fewest)} ${joiner} ${inWords(most)}`;
};

const PATH_SENTENCES: Record<SchedulingPath, string> = {
  concierge: "This step is on the concierge path, which leads to Resolve's bill-reduction service.",
  'ez-file-eligibility':
    'This step checks whether the visitor can use EZ File, the financial-aid filing path.',
  'ez-file-choice':
    'On this step the visitor chooses between EZ File, the financial-aid filing path, and the concierge path.',
};

const OFFER = 'schedule a free consultation with a dedicated representative';

/**
 * The scheduling sentences for the step named `stepName`, or undefined when
 * the table has no note for it. The sentences never depend on the visitor's
 * answers; they describe the questionnaire, not the visitor.
 */
export const describeScheduling = (stepName: StepName): string | undefined => {
  const note = schedulingNotes[stepName];
  if (!note) return undefined;

  let scheduling: string;
  if (note.offer === 'on-this-screen') {
    const condition = note.onlyWhen ? ` ${note.onlyWhen}` : '';
    scheduling = `Scheduling: the visitor can ${OFFER} on this screen${condition}.`;
  } else if (note.most === 1) {
    scheduling =
      `Scheduling: after this question the site usually offers to ${OFFER}; ` +
      "this varies with the visitor's answers, and not every visitor is offered one.";
  } else {
    scheduling =
      `Scheduling: ${describeRange(note.fewest, note.most)} more questions, counting this one, ` +
      `before the site offers to ${OFFER}; ` +
      "this varies with the visitor's answers, and not every visitor is offered one.";
  }
  return note.path ? `${scheduling} ${PATH_SENTENCES[note.path]}` : scheduling;
};
